
import {TraderDBAccount, SelectedAccount, Symbol} from '@qep/types/src';
import {useState, useEffect, useCallback} from 'react';
import {LinearProgress} from '@material-ui/core';
import {getUserAccountsFromTraderDB} from '../../services/accounts';
import {getAllAccountsForSymbol} from '../../utils';
import {traderDbAccountToSelectedAccount} from './utils';
import AccountSelector from './AccountSelector';
import AccountSelectModal from './AccountDetailsModal/AccountSelectModal';

export interface AccountSelectionProps {
  classes?: string;
  blockCustomization?: boolean;
  showSelectedLabel?: boolean;
  accountsConfig?: {
    accounts?: TraderDBAccount[];
    productForFiltering?: Symbol;
  };
  placeholder?: string;
  tradeable?: boolean;
  selectedAccount?: SelectedAccount;
  editingAccount?: boolean;
  allowEmpty?: boolean;
  onCancel?: () => void;
  onChange: (acc: SelectedAccount) => void;
}

const sortByDescription = accounts => accounts.sort((accA, accB) => accA?.description?.localeCompare(accB?.description));

export const AccountSelection = (props: AccountSelectionProps) => {
  const {accountsConfig, selectedAccount, editingAccount = false, placeholder, showSelectedLabel = true, tradeable = false, allowEmpty = false} = props;
  const [allAccounts, setAllAccounts] = useState<TraderDBAccount[]>(accountsConfig?.accounts || []);
  const [accounts, setAccounts] = useState<any[]>(accountsConfig?.accounts || []);
  const [accountSelectorValue, setAccountSelectorValue] = useState<SelectedAccount>();
  const [isLoading, setIsLoading] = useState<boolean>(!accountsConfig?.accounts);
  const [editAccountDetails, setEditAccountDetails] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [submittedEdit, setSubmittedEdit] = useState<boolean>(false);
  const [inlineAccountTextInvalidFields, setInlineAccountTextInvalidFields] = useState<Record<'memo' | 'customText2', boolean>>({
    memo: false,
    customText2: false,
  });

  /**
   * Resolves the best available account details payload for the edit modal.
   */
  const getAccountDetailsForModal = useCallback(() => {
    const fallbackAccount = accounts[0];
    const resolvedSelectedAccount = accountSelectorValue
      ?? selectedAccount
      ?? (fallbackAccount ? traderDbAccountToSelectedAccount(fallbackAccount) : undefined);
    if (!resolvedSelectedAccount?.account) {
      return null;
    }

    const hydratedAccount = allAccounts.find(account => account.id === resolvedSelectedAccount.account.id)
      ?? resolvedSelectedAccount.account;
    const accountDetails: TraderDBAccount & {
      accountId?: string;
      execCode?: string;
      tradeIntentionId?: string;
      notes?: string[];
    } = structuredClone(hydratedAccount) ?? {};

    // @ts-expect-error
    if (resolvedSelectedAccount.customText1) {
      accountDetails.customText1 = resolvedSelectedAccount.customText1;
    }

    if (resolvedSelectedAccount.customText2) {
      accountDetails.customText2 = resolvedSelectedAccount.customText2;
    }

    if (resolvedSelectedAccount.customText3) {
      accountDetails.customText3 = resolvedSelectedAccount.customText3;
    }

    if (resolvedSelectedAccount.memo) {
      accountDetails.memo = resolvedSelectedAccount.memo;
    }

    if (resolvedSelectedAccount?.tradeIntention) {
      accountDetails.tradeIntentionId = resolvedSelectedAccount.tradeIntention.id;
    }

    accountDetails.notes = [
      resolvedSelectedAccount.customText1 ?? resolvedSelectedAccount.tradeIntention?.tradeIntentionName ?? '',
      resolvedSelectedAccount.customText2 ?? resolvedSelectedAccount.account.customText2,
      resolvedSelectedAccount.customText3 ?? resolvedSelectedAccount.account.customText3,
    ];

    return {account: accountDetails};
  }, [accountSelectorValue, selectedAccount, accounts, allAccounts]);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        let tempAccounts: TraderDBAccount[] = await getUserAccountsFromTraderDB();
        if (tradeable) {
          tempAccounts = tempAccounts.filter(acc => acc.action === 'Trading').sort((a, b) => a.code.localeCompare(b.code));
        }

        setAllAccounts(sortByDescription(tempAccounts));

        if (accountsConfig?.productForFiltering) {
          const newAccounts = getAllAccountsForSymbol(tempAccounts, accountsConfig?.productForFiltering);
          if (newAccounts.length > 0) {
            tempAccounts = newAccounts;
            setAccounts(sortByDescription(tempAccounts));
          }
        } else {
          setAccounts(sortByDescription(tempAccounts));
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching accounts in account selector', error);
      }
    };

    if (!accountsConfig?.accounts && accounts.length === 0) {
      fetchAccounts();
    }
  }, []);

  useEffect(() => {
    if (accounts?.length !== 0) {
      setIsLoading(false);
    }

    if (accounts && accounts.length > 0 && !selectedAccount && !allowEmpty) {
      const startAccount: SelectedAccount = {
        account: accounts[0],
        tradeIntention: accounts[0].tradeIntentions[0],
        memo: accounts[0].memo,
        customText2: accounts[0].customText2,
        customText3: accounts[0].customText3,
      };
      setAccountSelectorValue(startAccount);
      props?.onChange(startAccount);
    }
  }, [accounts]);

  useEffect(() => {
    if (accountsConfig?.productForFiltering) {
      const newAccounts: TraderDBAccount[] = getAllAccountsForSymbol(allAccounts, accountsConfig?.productForFiltering);
      setAccounts(sortByDescription(newAccounts));
      if (allowEmpty) {
        newAccounts.unshift({
          id: '',
          code: 'No default strategy',
          description: 'No default strategy',
          tradeIntentions: [],
          customText2: '',
          customText3: '',
          memo: '',
          isActive: true,
          action: 'Trading',
          isGDP: true,
          neovestAccount: '',
          exchangeName: '',
          clearingString: '',
          lei: '',
          productSpecifications: [],
        });
      }

      if (!allowEmpty) {
        const currentAccount = newAccounts.find((acc: TraderDBAccount) => acc.id === selectedAccount?.account?.id);
        if (!currentAccount && newAccounts.length > 0) {
          props.onChange(traderDbAccountToSelectedAccount(newAccounts[0]));
        }
      }
    } else {
      setAccounts(sortByDescription(allAccounts));
    }
  }, [accountsConfig?.productForFiltering]);

  useEffect(() => {
    if (!selectedAccount) {
      return;
    }

    setAccountSelectorValue(selectedAccount);
    setSubmittedEdit(false);
    setEditAccountDetails(null);
  }, [selectedAccount]);

  useEffect(() => {
    setSubmittedEdit(false);

    if (editingAccount) {
      const nextEditAccountDetails = getAccountDetailsForModal();
      setEditAccountDetails(nextEditAccountDetails);
      setIsEditModalOpen(true);
    } else {
      setEditAccountDetails(null);
      setIsEditModalOpen(false);
    }
  }, [editingAccount, getAccountDetailsForModal]);

  return (
    <>
      {isLoading && <div className='loader-container'>
        <span>Loading accounts...</span>
        <div className='loader'><LinearProgress /></div>
      </div>}
      {!isLoading && <>
        <AccountSelectModal
          accounts={allAccounts}
          show={isEditModalOpen}
          leg={editAccountDetails}
          submitted={submittedEdit}
          onCancel={() => {
            setIsEditModalOpen(false);
            setEditAccountDetails(null);
            if (props.onCancel) {
              props.onCancel();
            }
          }}
          onContinue={(account: SelectedAccount) => {
            setAccountSelectorValue(account);
            setSubmittedEdit(true);
            setIsEditModalOpen(false);
            setEditAccountDetails(null);
            if (props.onChange) {
              props.onChange(account);
            }
          }}
        />
        <AccountSelector
          classes={props.classes}
          data-testid='account-selector'
          blockCustomization={props.blockCustomization}
          showSelectedLabel={showSelectedLabel}
          accounts={accounts}
          value={accountSelectorValue}
          invalidAccountTextFields={inlineAccountTextInvalidFields}
          setAccountTextFieldInvalid={(key, invalid) => {
            setInlineAccountTextInvalidFields(current => ({...current, [key]: invalid}));
          }}
          onChange={(value: SelectedAccount) => {
            setAccountSelectorValue(value);
            if (props.onChange) {
              props.onChange(value);
            }
          }}
          placeholder={placeholder}
        /></>}
    </>
  );
};
